import { buildAssignmentWindows } from "./assignmentWindows";
import { Location, Task, TaskStatus, TaskType } from "./types";
import { formatDateRangeShort, isoDateToLocalDate, toLocalISODate } from "./utils";
import {
  addDaysToDateKey,
  nowKeyInTimezone,
  parseDateKeyUTC,
  toDateKey,
  toDateKeyInTimezone,
} from "./timezoneDate";

export type AssignmentInfoByMachine = Record<
  string,
  { assignedAt?: string | null }
>;

export type CollectionRange = {
  startKey: string | null;
  startAt?: Date | null;
  useExactStart?: boolean;
  endKey: string | null;
  displayEndKey?: string | null;
  endAt?: Date | null;
  useExactEnd?: boolean;
  isPending?: boolean;
};

type TaskDateField = "doneAt" | "scheduledFor" | "createdAt";

type TaskMatchOptions = {
  types?: TaskType[];
  statuses?: TaskStatus[];
  requireDone?: boolean;
  dateFieldOrder?: TaskDateField[];
};

const defaultDateOrder: TaskDateField[] = [
  "doneAt",
  "scheduledFor",
  "createdAt",
];

const toDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toDayStart = (value?: string | null) => {
  if (!value) return null;
  const day = isoDateToLocalDate(value);
  return new Date(day.getFullYear(), day.getMonth(), day.getDate());
};

const startOfLocalDay = (value: Date) =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

const getTaskDate = (task: Task, order: TaskDateField[]): Date | null => {
  for (const field of order) {
    const value = task[field];
    if (field === "scheduledFor" && value) return isoDateToLocalDate(value);
    const parsed = toDate(value);
    if (parsed) return parsed;
  }
  return null;
};

const matchesTask = (task: Task, options: TaskMatchOptions) => {
  if (options.types?.length && !options.types.includes(task.type)) return false;
  if (options.requireDone && task.status !== "done") return false;
  if (options.statuses?.length && !options.statuses.includes(task.status)) {
    return false;
  }
  return true;
};

export function getCalendarDayDiff(
  startValue?: string | Date | null,
  endValue: string | Date = new Date()
): number | null {
  const start =
    startValue instanceof Date ? startValue : startValue ? toDate(String(startValue)) : null;
  const end = endValue instanceof Date ? endValue : toDate(String(endValue));
  if (!start || !end) return null;
  const startMs = parseDateKeyUTC(toLocalISODate(start)).getTime();
  const endMs = parseDateKeyUTC(toLocalISODate(end)).getTime();
  const diffMs = endMs - startMs;
  if (Number.isNaN(diffMs)) return null;
  return Math.max(0, Math.floor(diffMs / 86_400_000));
}

export function getAssignmentDurationDays(params: {
  assignedAt?: string | null;
  unassignedAt?: string | null;
  referenceDate?: Date;
}): number | null {
  const { assignedAt, unassignedAt, referenceDate } = params;
  return getCalendarDayDiff(assignedAt, unassignedAt || referenceDate || new Date());
}

export function buildAssignmentInfoByMachine(
  locations: Location[] = []
): AssignmentInfoByMachine {
  return locations.reduce<AssignmentInfoByMachine>((acc, location) => {
    const { currentAssignmentByMachine } = buildAssignmentWindows({
      machineAssignmentHistory: location.machineAssignmentHistory,
      machineAssignmentInfo: location.machineAssignmentInfo,
    });
    Object.entries(currentAssignmentByMachine).forEach(([machineId, value]) => {
      if (!machineId) return;
      const nextAssignedAt = value?.assignedAt ?? null;
      const currentAssignedAt = acc[machineId]?.assignedAt ?? null;
      if (!currentAssignedAt) {
        acc[machineId] = { assignedAt: nextAssignedAt };
        return;
      }
      if (!nextAssignedAt) return;
      const currentDate = new Date(currentAssignedAt);
      const nextDate = new Date(nextAssignedAt);
      if (
        !Number.isNaN(nextDate.getTime()) &&
        (Number.isNaN(currentDate.getTime()) || nextDate > currentDate)
      ) {
        acc[machineId] = { assignedAt: nextAssignedAt };
      }
    });
    return acc;
  }, {});
}

export function getLastTaskForMachine(
  tasks: Task[],
  machineId: string | undefined | null,
  options: TaskMatchOptions = {}
): Task | null {
  if (!machineId) return null;
  const order = options.dateFieldOrder || defaultDateOrder;
  let lastTask: Task | null = null;
  let lastDate: Date | null = null;
  for (const task of tasks) {
    if (task.machineId !== machineId) continue;
    if (!matchesTask(task, options)) continue;
    const taskDate = getTaskDate(task, order);
    if (!taskDate) continue;
    if (!lastDate || taskDate > lastDate) {
      lastDate = taskDate;
      lastTask = task;
    }
  }
  return lastTask;
}

export function getLastTaskForMachineSince(
  tasks: Task[],
  machineId: string | undefined | null,
  options: TaskMatchOptions = {},
  sinceDate?: string | null
): Task | null {
  if (!machineId) return null;
  const since = toDate(sinceDate);
  const order = options.dateFieldOrder || defaultDateOrder;
  let lastTask: Task | null = null;
  let lastDate: Date | null = null;
  for (const task of tasks) {
    if (task.machineId !== machineId) continue;
    if (!matchesTask(task, options)) continue;
    const taskDate = getTaskDate(task, order);
    if (!taskDate) continue;
    if (since && taskDate < since) continue;
    if (!lastDate || taskDate > lastDate) {
      lastDate = taskDate;
      lastTask = task;
    }
  }
  return lastTask;
}

export function getDaysSinceTask(
  task: Task | null | undefined,
  referenceDate: Date = new Date()
): number | null {
  const taskDate = task?.scheduledFor
    ? isoDateToLocalDate(task.scheduledFor)
    : toDate(task?.doneAt) || toDate(task?.createdAt);
  if (!taskDate) return null;
  return getCalendarDayDiff(taskDate, referenceDate);
}

export function getDaysSinceDate(
  value?: string | null,
  referenceDate: Date = new Date()
): number | null {
  return getCalendarDayDiff(value || null, referenceDate);
}

export function getDaysSinceLastTaskOrAssignment(params: {
  lastTask: Task | null | undefined;
  machineId?: string | null;
  assignmentInfo?: AssignmentInfoByMachine;
  referenceDate?: Date;
}): number | null {
  const { lastTask, machineId, assignmentInfo, referenceDate } = params;
  const ref = referenceDate || new Date();
  return (
    getDaysSinceTask(lastTask, ref) ??
    getDaysSinceDate(
      machineId ? assignmentInfo?.[machineId]?.assignedAt : undefined,
      ref
    )
  );
}

export function getDaysSinceLastDoneVisit(params: {
  tasks: Task[];
  machineId?: string | null;
  assignmentInfo?: AssignmentInfoByMachine;
  referenceDate?: Date;
  excludeTaskId?: string | null;
  includeReferenceDay?: boolean;
}): number | null {
  const {
    tasks,
    machineId,
    assignmentInfo,
    referenceDate = new Date(),
    excludeTaskId,
    includeReferenceDay = false,
  } = params;
  if (!machineId) return null;

  const refDay = startOfLocalDay(referenceDate).getTime();
  const assignedAt = assignmentInfo?.[machineId]?.assignedAt;
  const assignedDate = assignedAt ? toDate(assignedAt) : null;
  const assignedDay = assignedDate
    ? startOfLocalDay(assignedDate).getTime()
    : null;

  let lastTask: Task | null = null;
  let lastTaskDay = -Infinity;
  for (const task of tasks) {
    if (task.machineId !== machineId) continue;
    if (excludeTaskId && task.id === excludeTaskId) continue;
    if (task.status !== "done") continue;
    const taskDate = task.scheduledFor
      ? isoDateToLocalDate(task.scheduledFor)
      : toDate(task.doneAt) || toDate(task.createdAt);
    if (!taskDate) continue;
    const taskDay = startOfLocalDay(taskDate).getTime();
    if (includeReferenceDay ? taskDay > refDay : taskDay >= refDay) continue;
    if (assignedDay !== null && taskDay < assignedDay) continue;
    if (taskDay > lastTaskDay) {
      lastTaskDay = taskDay;
      lastTask = task;
    }
  }

  return getDaysSinceLastTaskOrAssignment({
    lastTask,
    machineId,
    assignmentInfo,
    referenceDate,
  });
}

export function getLocationDaysSinceLastTask(params: {
  tasks: Task[];
  machineIds: string[];
  assignmentInfo?: AssignmentInfoByMachine;
  referenceDate?: Date;
  mode?: "min" | "max";
  includeReferenceDay?: boolean;
}): number | null {
  const {
    tasks,
    machineIds,
    assignmentInfo,
    referenceDate,
    mode = "min",
    includeReferenceDay = false,
  } = params;
  const ref = referenceDate || new Date();
  const values = machineIds
    .map((machineId) => {
      return getDaysSinceLastDoneVisit({
        tasks,
        machineId,
        assignmentInfo,
        referenceDate: ref,
        includeReferenceDay,
      });
    })
    .filter((value): value is number => value !== null);
  if (!values.length) return null;
  return mode === "max" ? Math.max(...values) : Math.min(...values);
}

export function buildCollectionRange(params: {
  previousScheduledFor?: string | null;
  assignedAt?: string | null;
  scheduledFor?: string | null;
  isPending?: boolean;
  timezone?: string;
}): CollectionRange {
  const previousKey = toDateKey(params.previousScheduledFor) || null;
  const assignedKey = toDateKeyInTimezone(params.assignedAt, params.timezone) || null;
  const assignedAtTime = toDate(params.assignedAt);
  const todayKey = nowKeyInTimezone(params.timezone);
  const endKey = params.isPending
    ? addDaysToDateKey(todayKey, 1)
    : toDateKey(params.scheduledFor) || null;
  const displayEndKey = params.isPending ? todayKey : endKey;
  return {
    startKey: previousKey || assignedKey,
    startAt: previousKey
      ? toDayStart(previousKey)
      : assignedAtTime || (assignedKey ? toDayStart(assignedKey) : null),
    useExactStart: Boolean(!previousKey && assignedAtTime),
    endKey,
    displayEndKey,
    endAt: params.isPending ? new Date() : endKey ? toDayStart(endKey) : null,
    isPending: Boolean(params.isPending),
  };
}

export function getCollectionRangeLabel(range?: CollectionRange | null) {
  if (!range?.startKey || !range?.endKey) return "—";
  return formatDateRangeShort(range.startKey, range.endKey);
}

export function getCollectionRangeDays(range?: CollectionRange | null) {
  if (!range?.startKey || !range?.endKey) return null;
  const start = parseDateKeyUTC(range.startKey);
  const end = parseDateKeyUTC(range.endKey);
  const diffMs = end.getTime() - start.getTime();
  if (Number.isNaN(diffMs)) return null;
  return Math.max(0, Math.floor(diffMs / 86_400_000));
}
